import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useTheme } from '@mui/material/styles';

import * as amplitude from '@amplitude/analytics-browser';

import SkipConfirmation from '../components/SkipConfirmation';
import { useAppState } from '../contexts/AppStateProvider';
// Use new hooks instead of legacy context
import { useJWT } from '../contexts/JWTProvider';
import { ServiceThemeOptions } from '../themes';
import { tsps } from '../tsps';
import { createHoverBackground } from '../utils/ThemeUtils';
import { Analytics, createAnalyticsContext } from '../utils/analytics';
import { SkipFeedbackMeta, SkipFeedbackPayload } from '../utils/skipFeedback';

export default function SelectTSP() {
  const theme = useTheme() as ServiceThemeOptions;
  const navigate = useNavigate();
  const location = useLocation();
  const { search } = location;
  const [searchFilter, setSearchFilter] = useState('');
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);

  // Use new centralized hooks
  const jwt = useJWT();
  const {
    approved,
    setTSP,
    existingTSPs,
    setUpdatingTSP,
    serviceName,
    tspCount,
  } = useAppState();

  // Extract theme values safely
  const primaryColor = (theme.palette?.primary as any)?.main || '#B60207';
  const paperBgColor = theme.palette?.background?.paper || '#FFFFFF';
  const textPrimaryColor = theme.palette?.text?.primary || '#000000';

  useEffect(() => {
    if (!jwt.isValid) {
      // Let JWT handlers determine appropriate error message and actions
      navigate('/error');
      return;
    }

    // Track page view (Analytics handles both Amplitude and Slack)
    const analyticsContext = createAnalyticsContext(jwt, serviceName);
    Analytics.pageView(analyticsContext, 'SelectTSP');

    if (!approved) {
      navigate({
        pathname: '/',
        search: search,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    jwt.isValid,
    approved,
    navigate,
    search,
    serviceName,
    // Include specific JWT properties used for analytics
    jwt.serviceId,
    jwt.fleetCode,
    jwt.rawToken,
  ]); // Performance optimized: specific JWT properties included for analytics context

  // Handle provider not found - navigate to MissingELD page
  const handleCantFindProvider = () => {
    navigate('/missingELD');
  };

  // Handle opening skip confirmation dialog
  const handleSkipClick = () => {
    console.log(
      '[SelectTSP] handleSkipClick - tspCount:',
      tspCount,
      'existingTSPs:',
      existingTSPs
    );

    // If user already has connected devices, take them directly to success page
    if (tspCount > 0) {
      console.log(
        '[SelectTSP] User has existing devices, going to success page'
      );
      // Track skip action for users with existing devices (without redirect)
      amplitude.track('User Skipped Adding Additional Devices', {
        tsp: 'none',
        serviceName: serviceName,
        existingDeviceCount: tspCount,
      });

      navigate('/success', {
        state: {
          successMsg: 'Accounts connected successfully!',
          successCaption:
            'Your telematics data is now connected and ready to use.',
        },
      });
    } else {
      console.log('[SelectTSP] First-time user, showing confirmation dialog');
      // For first-time users, show the confirmation dialog
      setSkipDialogOpen(true);
    }
  };

  // Handle actual skip action after confirmation
  const handleSkipConfirmed = async (
    feedback?: SkipFeedbackPayload,
    meta?: SkipFeedbackMeta
  ) => {
    setSkipDialogOpen(false);

    // Track confirmed skip with analytics
    const analyticsContext = createAnalyticsContext(jwt, serviceName, {
      navigate: (path: string) => navigate(path),
      skipReasonParam: meta?.reasonLabel?.trim()
        ? meta.reasonLabel?.trim()
        : meta?.reasonValue || undefined,
      skipTspParam: feedback?.tsp_name?.trim() || undefined,
    });
    await Analytics.confirmedSkip.tspSelectionQuit(analyticsContext);
  };

  // Handle cancel of skip dialog
  const handleCloseSkipDialog = () => {
    setSkipDialogOpen(false);
  };

  // Updated TSP item click handler
  const handleTSPSelect = (item: any) => {
    // Track TSP selection (Analytics handles both Amplitude and Slack)
    const analyticsContext = createAnalyticsContext(jwt, serviceName);
    Analytics.tspSelected(analyticsContext, item.key, item.name);

    setUpdatingTSP(existingTSPs.includes(item.key));
    setTSP(item.key);

    navigate({
      pathname: `/config/${item.key}`,
      search: search, // Preserve all URL parameters including 'from'
    });
  };

  const listOfTSPs = tsps
    .filter(function (el: any) {
      if (searchFilter !== '') {
        return (
          el.key.toLowerCase().includes(searchFilter.toLowerCase()) ||
          el.name.toLowerCase().includes(searchFilter.toLowerCase())
        );
      } else {
        return true;
      }
    })
    .map((item: any) => (
      <Grid2
        key={item.key}
        xs={6}
        onClick={() => handleTSPSelect(item)}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 0.5, sm: 1 },
          minHeight: { xs: '120px', sm: '140px' },
          maxHeight: { xs: '120px', sm: '140px' }, // Prevent boxes from stretching when fewer results
          textAlign: 'center',
          borderRadius: '8px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            cursor: 'pointer',
            backgroundColor:
              (theme.palette?.action?.hover as string) || 'rgba(0, 0, 0, 0.04)',
            transform: 'scale(1.02)',
          },
        }}
      >
        <Box
          component='img'
          src={`/images/${
            item.imageName || item.name
          }.png?w=248&fit=crop&auto=format`}
          alt={item.name}
          loading='lazy'
          sx={{
            maxHeight: { xs: '60px', sm: '75px' },
            maxWidth: { xs: '80px', sm: '100px' },
            objectFit: 'contain',
          }}
        />
        <Typography
          variant='subtitle2'
          sx={{
            mt: { xs: 0.5, sm: 1 },
            fontWeight: 600,
            fontSize: { xs: '12px', sm: '14px' },
            lineHeight: { xs: '16px', sm: '20px' },
            color: textPrimaryColor,
          }}
        >
          {item.name}
        </Typography>
      </Grid2>
    ));

  return (
    <>
      <Box
        sx={{
          // Mobile: Remove container, content fills screen
          display: { xs: 'block', sm: 'flex' },
          flexDirection: { xs: 'column', sm: 'column' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: { xs: 'flex-start', sm: 'center' },
          minHeight: '100vh',
          py: { xs: 0, sm: 0 },
          px: { xs: 0, sm: 2 },
          pt: { xs: 0, sm: 0 },
        }}
      >
        <Paper
          elevation={3}
          sx={{
            // Mobile: Full screen, no card appearance
            width: { xs: '100%', sm: '545px' },
            minWidth: { xs: '100vw', sm: '545px' },
            maxWidth: { xs: '100vw', sm: 'calc(100% - 32px)' },
            minHeight: { xs: '100vh', sm: 'auto' }, // Full height on mobile
            borderRadius: { xs: '0', sm: '4px' },
            overflow: 'hidden',
            mb: { xs: 0, sm: 2 },
            mx: { xs: 0, sm: 'auto' },
            bgcolor: paperBgColor,
            // Remove elevation/shadow on mobile for true full-screen feel
            boxShadow: { xs: 'none', sm: 3 },
          }}
        >
          {/* Main content area */}
          <Box
            sx={{
              padding: { xs: '24px 16px 0px', sm: '32px 50px 0px' },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              bgcolor: paperBgColor,
              minWidth: { xs: 'calc(100vw - 48px)', sm: 'auto' },
              width: '100%',
            }}
          >
            <Typography
              variant='h5'
              align='center'
              sx={{
                mb: 2,
                fontWeight: 600,
                fontSize: { xs: '22px', sm: '28px' },
                lineHeight: { xs: '28px', sm: '36px' },
                maxWidth: '445px',
                wordWrap: 'break-word',
                hyphens: 'auto',
              }}
            >
              Link your telematics provider
            </Typography>

            <Box
              sx={{
                width: '100%',
                maxWidth: '445px',
                mb: 3,
                position: 'relative',
              }}
            >
              <TextField
                id='search-input'
                label='Search'
                variant='outlined'
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon sx={{ color: '#5C656A', fontSize: '20px' }} />
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => {
                  const searchValue = e.target.value;
                  setSearchFilter(searchValue);
                }}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    paddingLeft: '12px',
                    height: { xs: '48px', sm: '56px' },
                    '& fieldset': {
                      borderColor: '#263238',
                      borderWidth: '1px',
                      top: 0,
                      borderRadius: '8px',
                    },
                    '&:hover fieldset': {
                      borderColor:
                        theme.custom?.inputField?.hover?.borderColor ||
                        '#263238',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor:
                        theme.custom?.inputField?.focus?.borderColor ||
                        primaryColor,
                      borderWidth:
                        theme.custom?.inputField?.focus?.borderWidth || '2px',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    backgroundColor: paperBgColor,
                    padding: '0 4px',
                    lineHeight: '14px',
                    '&.Mui-focused': {
                      color:
                        theme.custom?.inputField?.focus?.borderColor ||
                        primaryColor,
                    },
                  },
                  '& .MuiInputLabel-shrink': {
                    transform: 'translate(10px, 0px) scale(0.75)',
                  },
                  '& .MuiInputAdornment-root': {
                    marginRight: '8px',
                  },
                  '& input': {
                    fontSize: { xs: '16px', sm: '16px' },
                    fontWeight: 400,
                    letterSpacing: '0.15px',
                    paddingLeft: '0px',
                  },
                }}
              />
            </Box>

            <Box
              sx={{
                width: '100%',
                maxWidth: '445px',
                minWidth: { xs: '100%', sm: '445px' },
                height: { xs: '300px', sm: '400px' },
                overflowY: 'auto',
                mb: { xs: 3, sm: 4 },
                px: { xs: 0, sm: 0 },
              }}
            >
              <Grid2
                container
                spacing={{ xs: 1, sm: 2 }}
                sx={{
                  width: '100%',
                  minWidth: '100%',
                  minHeight: '100%',
                  display: 'flex',
                  flexWrap: 'wrap',
                  m: 0,
                }}
              >
                {listOfTSPs}
                {listOfTSPs.length === 0 && (
                  <Box
                    sx={{
                      width: '100%',
                      minWidth: '100%',
                      height: '100%',
                      minHeight: { xs: '280px', sm: '380px' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'text.secondary',
                      fontSize: '14px',
                      flexShrink: 0,
                    }}
                  >
                    No providers found
                  </Box>
                )}
              </Grid2>
            </Box>
          </Box>

          {/* Footer with buttons */}
          <Box
            sx={{
              bgcolor: paperBgColor,
              padding: { xs: '16px 16px 24px', sm: '16px 50px 24px' },
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '10px',
              boxShadow:
                '0px 1px 4px rgba(0, 0, 0, 0.01), 0px 4px 8px rgba(0, 0, 0, 0.02), 0px 1px 12px rgba(0, 0, 0, 0.12)',
            }}
          >
            <Button
              variant='outlined'
              onClick={handleCantFindProvider}
              sx={{
                height: { xs: '48px', sm: '40px' },
                width: '100%',
                maxWidth: { xs: '100%', sm: '392px' },
                color:
                  theme.custom?.buttons?.outlined?.active?.textColor ||
                  primaryColor,
                borderColor:
                  theme.custom?.buttons?.outlined?.active?.borderColor ||
                  '#C7CACB',
                backgroundColor:
                  theme.custom?.buttons?.outlined?.active?.backgroundColor ||
                  'transparent',
                borderRadius: '4px',
                textTransform: 'uppercase',
                fontWeight: 700,
                fontSize: { xs: '16px', sm: '14px' },
                '&:hover': {
                  borderColor:
                    theme.custom?.buttons?.outlined?.hover?.borderColor ||
                    primaryColor,
                  color:
                    theme.custom?.buttons?.outlined?.hover?.textColor ||
                    primaryColor,
                  backgroundColor:
                    theme.custom?.buttons?.outlined?.hover?.backgroundColor ||
                    createHoverBackground(primaryColor, 0.04),
                },
                '&:focus': {
                  outline: 'none',
                },
                '&:focus-visible': {
                  borderColor:
                    theme.custom?.buttons?.outlined?.focus?.borderColor ||
                    primaryColor,
                  color:
                    theme.custom?.buttons?.outlined?.focus?.textColor ||
                    primaryColor,
                  backgroundColor:
                    theme.custom?.buttons?.outlined?.focus?.backgroundColor ||
                    createHoverBackground(primaryColor, 0.04),
                  boxShadow:
                    theme.custom?.buttons?.outlined?.focus?.boxShadow ||
                    `0 0 0 2px ${createHoverBackground(primaryColor, 0.2)}`,
                },
                '&:active': {
                  borderColor:
                    theme.custom?.buttons?.outlined?.press?.borderColor ||
                    primaryColor,
                  color:
                    theme.custom?.buttons?.outlined?.press?.textColor ||
                    primaryColor,
                  backgroundColor:
                    theme.custom?.buttons?.outlined?.press?.backgroundColor ||
                    createHoverBackground(primaryColor, 0.08),
                },
                '&:disabled': {
                  borderColor:
                    theme.custom?.buttons?.outlined?.disabled?.borderColor ||
                    '#E0E0E0',
                  color:
                    theme.custom?.buttons?.outlined?.disabled?.textColor ||
                    '#C7CACB',
                  backgroundColor:
                    theme.custom?.buttons?.outlined?.disabled
                      ?.backgroundColor || 'transparent',
                },
              }}
            >
              I CAN'T FIND MY PROVIDER
            </Button>

            <Button
              variant='text'
              onClick={handleSkipClick}
              sx={{
                height: { xs: '48px', sm: '40px' },
                width: '100%',
                maxWidth: { xs: '100%', sm: '392px' },
                color:
                  theme.custom?.buttons?.text?.active?.textColor ||
                  primaryColor,
                backgroundColor:
                  theme.custom?.buttons?.text?.active?.backgroundColor ||
                  'transparent',
                textTransform: 'uppercase',
                fontWeight: 700,
                fontSize: { xs: '16px', sm: '14px' },
                lineHeight: '16px',
                letterSpacing: '0.75px',
                padding: '12px',
                outline: 'none',
                border: 'none',
                boxShadow: 'none',
                '&:hover': {
                  color:
                    theme.custom?.buttons?.text?.hover?.textColor ||
                    primaryColor,
                  backgroundColor:
                    theme.custom?.buttons?.text?.hover?.backgroundColor ||
                    createHoverBackground(primaryColor, 0.08),
                },
                '&:focus': {
                  outline: 'none',
                  border: 'none',
                  boxShadow: 'none',
                },
                '&:focus-visible': {
                  color:
                    theme.custom?.buttons?.text?.focus?.textColor ||
                    primaryColor,
                  backgroundColor:
                    theme.custom?.buttons?.text?.focus?.backgroundColor ||
                    createHoverBackground(primaryColor, 0.08),
                  boxShadow:
                    theme.custom?.buttons?.text?.focus?.boxShadow ||
                    `0 0 0 2px ${createHoverBackground(primaryColor, 0.2)}`,
                  outline: 'none',
                  border: 'none',
                },
                '&:active': {
                  color:
                    theme.custom?.buttons?.text?.press?.textColor ||
                    primaryColor,
                  backgroundColor:
                    theme.custom?.buttons?.text?.press?.backgroundColor ||
                    createHoverBackground(primaryColor, 0.12),
                },
                '&:disabled': {
                  color:
                    theme.custom?.buttons?.text?.disabled?.textColor ||
                    '#C7CACB',
                  backgroundColor:
                    theme.custom?.buttons?.text?.disabled?.backgroundColor ||
                    'transparent',
                },
              }}
            >
              {tspCount > 0
                ? 'SKIP ADDING ADDITIONAL DEVICES'
                : 'SKIP LINKING TELEMATICS'}
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* Skip Confirmation Dialog */}
      <SkipConfirmation
        open={skipDialogOpen}
        onClose={handleCloseSkipDialog}
        onSkip={handleSkipConfirmed}
        serviceName={serviceName}
      />
    </>
  );
}
